import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import {
  PAGE_CONTAINER_MAX_WIDTH,
  documentPageCanvasSx,
  documentPageContainerSx,
  documentSolidButtonSx,
  documentTextButtonSx,
  DOCUMENT,
} from '../constants/documentTheme';
import api from '../config/api';
import {
  formatImportDestinationLabel,
  formatTripOptionLabel,
  summarizeImportReservations,
} from '../utils/emailImportDisplay';

const EmailImports = ({ variant = 'traveler' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isPartner = variant === 'partner';
  const authOpts = useMemo(() => ({ usePartnerAuth: isPartner }), [isPartner]);
  const [imports, setImports] = useState([]);
  const [trips, setTrips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [resolvingId, setResolvingId] = useState(null);
  const [selectedTripByImport, setSelectedTripByImport] = useState({});
  const [placementByImport, setPlacementByImport] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [highlightId, setHighlightId] = useState(null);
  const rowRefs = useRef({});

  const tripById = useMemo(() => {
    const map = new Map();
    trips.forEach((trip) => map.set(trip.id, trip));
    return map;
  }, [trips]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [importsRes, tripsRes] = await Promise.all([
        api.get('/inbound-email/imports', authOpts),
        api.get('/inbound-email/candidate-trips', authOpts),
      ]);
      setImports(importsRes.data.imports || []);
      setTrips(tripsRes.data.trips || []);
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.loadError'));
    } finally {
      setLoading(false);
    }
  }, [t, authOpts]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const undoToken = searchParams.get('undo');
    if (!undoToken) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await api.post('/inbound-email/undo-by-token', { token: undoToken }, authOpts);
        if (!cancelled) {
          setSuccess(t('emailForward.undoSuccess'));
          setSearchParams({});
          load();
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || t('emailForward.undoError'));
        }
      }
    })();
    return () => { cancelled = true; };
  }, [searchParams, setSearchParams, load, t, authOpts]);

  useEffect(() => {
    const reviewId = parseInt(searchParams.get('review'), 10);
    if (!Number.isInteger(reviewId) || loading) return undefined;
    setHighlightId(reviewId);
    const timer = setTimeout(() => {
      const node = rowRefs.current[reviewId];
      if (node?.scrollIntoView) {
        node.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete('review');
        return next;
      }, { replace: true });
    }, 50);
    return () => clearTimeout(timer);
  }, [searchParams, setSearchParams, loading, imports]);

  const handleResolve = async (importId) => {
    const tripId = selectedTripByImport[importId];
    if (!tripId) {
      setError(t('emailForward.selectTrip'));
      return;
    }
    setResolvingId(importId);
    setError('');
    try {
      const payload = { tripId };
      if (isPartner) {
        payload.placement = placementByImport[importId] || 'personal';
      }
      await api.post(`/inbound-email/imports/${importId}/resolve`, payload, authOpts);
      setSuccess(t('emailForward.resolveSuccess'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.resolveError'));
    } finally {
      setResolvingId(null);
    }
  };

  const handleUndo = async (importId) => {
    setBusyId(importId);
    setError('');
    try {
      await api.post(`/inbound-email/imports/${importId}/undo`, {}, authOpts);
      setSuccess(t('emailForward.undoSuccess'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.undoError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleRetry = async (importId) => {
    setBusyId(importId);
    setError('');
    try {
      await api.post(`/inbound-email/imports/${importId}/reprocess`, {}, authOpts);
      setSuccess(t('emailForward.retrySuccess'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.retryError'));
    } finally {
      setBusyId(null);
    }
  };

  const handleDismiss = async (importId) => {
    setBusyId(importId);
    setError('');
    try {
      await api.post(`/inbound-email/imports/${importId}/dismiss`, {}, authOpts);
      setSuccess(t('emailForward.dismissSuccess'));
      await load();
    } catch (err) {
      setError(err.response?.data?.error || t('emailForward.dismissError'));
    } finally {
      setBusyId(null);
    }
  };

  const statusColor = (status) => {
    if (status === 'imported') return 'success';
    if (status === 'needs_review' || status === 'received') return 'warning';
    if (status === 'failed' || status === 'rejected') return 'error';
    return 'default';
  };

  const openTrip = (tripId) => {
    navigate(isPartner ? `/partner/trips/${tripId}` : `/trips/${tripId}`);
  };

  const assignedTripLabel = (item) => {
    if (!item.trip_title && !item.trip_id) return '';
    const fromList = item.trip_id ? tripById.get(item.trip_id) : null;
    return formatImportDestinationLabel({
      ...item,
      trip_title: item.trip_title || fromList?.title,
      trip_start_date: item.trip_start_date || fromList?.start_date,
      trip_end_date: item.trip_end_date || fromList?.end_date,
    }, t);
  };

  return (
    <Box sx={documentPageCanvasSx}>
      <Container maxWidth={PAGE_CONTAINER_MAX_WIDTH} sx={{ ...documentPageContainerSx, py: { xs: 4, md: 5 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap', pb: 2, borderBottom: `1px solid ${DOCUMENT.ink}`, mb: 3 }}>
          <Typography component="h1" sx={{ fontFamily: DOCUMENT.display, fontSize: { xs: 28, md: 36 }, color: DOCUMENT.ink }}>
            {t('emailForward.importsHeading')}
          </Typography>
          <Button
            onClick={() => navigate(isPartner ? '/partner/trips' : '/trips')}
            sx={{ ...documentTextButtonSx, ml: 'auto' }}
          >
            {t(isPartner ? 'emailForward.backToPartnerTrips' : 'emailForward.backToTrips')}
          </Button>
        </Box>

        {isPartner && (
          <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 2, lineHeight: 1.5 }}>
            {t('emailForward.partnerImportsHint')}
          </Typography>
        )}

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

        {loading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : imports.length === 0 ? (
          <Typography sx={{ color: DOCUMENT.ink3 }}>{t('emailForward.noImports')}</Typography>
        ) : (
          <Stack spacing={2}>
            {imports.map((item) => {
              const bookingLines = summarizeImportReservations(item);
              const tripLabel = assignedTripLabel(item);
              return (
                <Box
                  key={item.id}
                  ref={(el) => { rowRefs.current[item.id] = el; }}
                  data-testid={`email-import-${item.id}`}
                  sx={{
                    p: 2.5,
                    bgcolor: DOCUMENT.surface,
                    border: `1px solid ${highlightId === item.id ? DOCUMENT.accent : DOCUMENT.ruleHard}`,
                    boxShadow: highlightId === item.id ? `0 0 0 2px ${DOCUMENT.accent}` : 'none',
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
                    <Chip size="small" label={item.status} color={statusColor(item.status)} />
                    <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 18, color: DOCUMENT.ink }}>
                      {item.subject || t('emailForward.noSubject')}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1 }}>
                    {item.from_email} · {new Date(item.created_at).toLocaleString()}
                    {tripLabel ? ` · ${tripLabel}` : ''}
                  </Typography>

                  {bookingLines.length > 0 ? (
                    <Box sx={{ mb: 1.5 }} data-testid={`email-import-bookings-${item.id}`}>
                      <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mb: 0.5, letterSpacing: 0.02 }}>
                        {t('emailForward.bookingDetails')}
                      </Typography>
                      <Stack spacing={0.5}>
                        {bookingLines.map((line) => (
                          <Typography
                            key={line}
                            sx={{ fontSize: 14, color: DOCUMENT.ink, lineHeight: 1.45 }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  ) : (
                    item.status === 'needs_review' && (
                      <Typography sx={{ fontSize: 13, color: DOCUMENT.ink3, mb: 1.5 }}>
                        {t('emailForward.noBookingDetails')}
                      </Typography>
                    )
                  )}

                  {item.error && (
                    <Typography sx={{ fontSize: 13, color: '#8C2B22', mb: 1 }}>{item.error}</Typography>
                  )}

                  {item.status === 'needs_review' && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1.5 }}>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <FormControl size="small" sx={{ minWidth: 280, maxWidth: '100%' }}>
                          <InputLabel id={`trip-select-${item.id}`}>{t('emailForward.chooseTrip')}</InputLabel>
                          <Select
                            labelId={`trip-select-${item.id}`}
                            label={t('emailForward.chooseTrip')}
                            value={selectedTripByImport[item.id] || ''}
                            onChange={(e) => setSelectedTripByImport((prev) => ({
                              ...prev,
                              [item.id]: e.target.value,
                            }))}
                          >
                            {trips.map((trip) => (
                              <MenuItem key={trip.id} value={trip.id}>
                                {formatTripOptionLabel(trip)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <Button
                          sx={documentSolidButtonSx}
                          disabled={resolvingId === item.id}
                          onClick={() => handleResolve(item.id)}
                        >
                          {resolvingId === item.id ? t('emailForward.resolving') : t('emailForward.assignTrip')}
                        </Button>
                      </Box>
                      {isPartner && (
                        <FormControl component="fieldset" data-testid={`email-import-placement-${item.id}`}>
                          <Typography sx={{ fontSize: 12, color: DOCUMENT.ink3, mb: 0.5 }}>
                            {t('emailForward.placementLabel')}
                          </Typography>
                          <RadioGroup
                            row
                            value={placementByImport[item.id] || 'personal'}
                            onChange={(e) => setPlacementByImport((prev) => ({
                              ...prev,
                              [item.id]: e.target.value,
                            }))}
                          >
                            <FormControlLabel
                              value="personal"
                              control={<Radio size="small" />}
                              label={t('emailForward.placementPersonal')}
                            />
                            <FormControlLabel
                              value="shared"
                              control={<Radio size="small" />}
                              label={t('emailForward.placementShared')}
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </Box>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 1.5 }}>
                    {item.status === 'imported' && item.trip_id && (
                      <Button sx={documentTextButtonSx} onClick={() => openTrip(item.trip_id)}>
                        {t('emailForward.openTrip')}
                      </Button>
                    )}
                    {item.status === 'imported' && (
                      <Button
                        sx={documentTextButtonSx}
                        disabled={busyId === item.id}
                        onClick={() => handleUndo(item.id)}
                      >
                        {t('emailForward.undo')}
                      </Button>
                    )}
                    {(item.status === 'failed' || item.status === 'received') && (
                      <Button
                        sx={documentSolidButtonSx}
                        disabled={busyId === item.id}
                        onClick={() => handleRetry(item.id)}
                      >
                        {busyId === item.id ? t('emailForward.retrying') : t('emailForward.retry')}
                      </Button>
                    )}
                    <Button
                      sx={documentTextButtonSx}
                      disabled={busyId === item.id}
                      onClick={() => handleDismiss(item.id)}
                      data-testid={`email-import-dismiss-${item.id}`}
                    >
                      {t('emailForward.dismiss')}
                    </Button>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Container>
    </Box>
  );
};

export default EmailImports;
